import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, BookOpen, ChevronDown, ChevronUp, Quote, MessageSquare, User } from "lucide-react";
import axios from "axios";
import REFLECTIONS from "../data/reflections";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReflectionsPage() {
  const [expandedId, setExpandedId] = useState(null);
  const [stories, setStories] = useState([]);
  const [loadingStories, setLoadingStories] = useState(true);

  useEffect(() => {
    axios.get(`${API}/stories`)
      .then(res => setStories(res.data || []))
      .catch(() => {})
      .finally(() => setLoadingStories(false));
  }, []);

  const toggle = (id) => setExpandedId(prev => prev === id ? null : id);

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg)' }}>
      <Helmet>
        <title>Reflections | The Reading Room</title>
        <meta name="description" content="Thoughtful reflections on books that stayed with us. Curated reading insights from The Reading Room." />
        <link rel="canonical" href="https://thereading-room.com/reflections" />
        <meta property="og:title" content="Reflections That Stayed With Me | The Reading Room" />
        <meta property="og:description" content="Thoughtful reflections on books that stayed with us. Curated reading insights from The Reading Room." />
        <meta property="og:image" content="https://thereading-room.com/og-reflections.jpg" />
        <meta property="og:url" content="https://thereading-room.com/reflections" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Reflections That Stayed With Me | The Reading Room" />
        <meta name="twitter:image" content="https://thereading-room.com/og-reflections.jpg" />
      </Helmet>

      {/* Header */}
      <header className="sticky top-0 z-50 px-5 md:px-10 py-4" style={{ background: 'rgba(245,240,232,0.92)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-sm font-medium transition-opacity hover:opacity-70" style={{ color: 'var(--muted2)' }} data-testid="reflections-back-home">
            <ArrowLeft className="w-4 h-4" /> Home
          </Link>
          <Link to="/" className="text-lg font-bold" style={{ fontFamily: "'Playfair Display', serif", color: 'var(--text)' }} data-testid="reflections-logo">
            The Reading Room
          </Link>
          <Link to="/browse" className="text-sm font-medium transition-opacity hover:opacity-70" style={{ color: 'var(--muted2)' }} data-testid="reflections-browse-link">
            Browse
          </Link>
        </div>
      </header>

      {/* Hero */}
      <div className="px-5 md:px-10 pt-12 pb-8">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4" style={{ fontFamily: "'Playfair Display', serif", color: 'var(--text)' }} data-testid="reflections-heading">
            Reflections That Stayed With Me
          </h1>
          <p className="text-base max-w-xl mx-auto leading-relaxed" style={{ color: 'var(--muted)' }}>
            Some books leave a quiet mark. These are personal reflections on reads that changed how I think, work, and see the world.
          </p>
        </div>
      </div>

      {/* Reflections List */}
      <div className="px-5 md:px-10 pb-16">
        <div className="max-w-4xl mx-auto space-y-6">
          {REFLECTIONS.map((ref) => {
            const isExpanded = expandedId === ref.id;
            return (
              <article
                key={ref.id}
                className="rounded-2xl overflow-hidden transition-all duration-300"
                style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)', boxShadow: '0 2px 20px rgba(0,0,0,0.04)' }}
                data-testid={`reflection-article-${ref.id}`}
              >
                {/* Card Header — always visible */}
                <div
                  className="flex flex-col sm:flex-row gap-5 p-6 sm:p-8 cursor-pointer group"
                  onClick={() => toggle(ref.id)}
                  data-testid={`reflection-toggle-${ref.id}`}
                >
                  <ReflectionCover cover={ref.cover} title={ref.title} />
                  <div className="flex-1 min-w-0 flex flex-col justify-center">
                    <span className="inline-block self-start px-3 py-1 rounded-full text-[10px] font-semibold mb-3" style={{ background: 'var(--highlight-muted)', color: 'var(--muted)' }}>
                      {ref.theme}
                    </span>
                    <h2 className="text-xl sm:text-2xl font-bold mb-1 leading-snug" style={{ fontFamily: "'Playfair Display', serif", color: 'var(--text)' }}>
                      {ref.title}
                    </h2>
                    <p className="text-sm mb-3" style={{ color: 'var(--highlight)' }}>by {ref.author}</p>
                    <p className="text-sm leading-relaxed" style={{ color: 'var(--muted)' }}>
                      {ref.intro}
                    </p>
                    <button
                      className="flex items-center gap-1.5 mt-4 text-sm font-semibold transition-colors group-hover:opacity-80"
                      style={{ color: 'var(--text-secondary)' }}
                      data-testid={`reflection-expand-${ref.id}`}
                    >
                      {isExpanded ? "Collapse" : "Read Full Reflection"}
                      {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* Expanded Content */}
                {isExpanded && (
                  <div className="px-6 sm:px-8 pb-8 animate-in fade-in duration-300" data-testid={`reflection-content-${ref.id}`}>
                    <div className="border-t pt-6 mb-6" style={{ borderColor: 'rgba(0,0,0,0.06)' }} />

                    {/* Full Reflection Paragraphs */}
                    <div className="space-y-4 mb-8">
                      {ref.fullReflection.map((p, i) => (
                        <p key={i} className="text-sm leading-[1.8]" style={{ color: 'var(--text)' }}>{p}</p>
                      ))}
                    </div>

                    {/* Key Takeaways */}
                    {ref.takeaways.length > 0 && (
                      <div className="mb-8">
                        <h3 className="text-lg font-bold mb-5" style={{ fontFamily: "'Playfair Display', serif", color: 'var(--text)' }}>
                          Key Takeaways
                        </h3>
                        <div className="space-y-4">
                          {ref.takeaways.map((t, i) => (
                            <div key={i} className="rounded-xl p-5" style={{ background: 'rgba(245,240,232,0.7)', border: '1px solid var(--card-border)' }}>
                              <p className="font-semibold text-sm mb-1.5" style={{ color: 'var(--text)' }}>{t.title}</p>
                              {t.body && <p className="text-sm leading-relaxed" style={{ color: 'var(--muted)' }}>{t.body}</p>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Closing Line */}
                    <div className="rounded-xl p-5 flex gap-3 mb-5" style={{ background: 'rgba(209,168,110,0.08)', border: '1px solid var(--highlight-border-light)' }}>
                      <Quote className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--highlight)' }} />
                      <p className="text-sm italic leading-relaxed" style={{ color: 'var(--muted2)' }}>
                        {ref.closingLine}
                      </p>
                    </div>
                    {/* Share buttons */}
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="text-[11px] font-medium" style={{ color: 'var(--muted)' }}>Share this reflection:</span>
                      <button
                        data-testid={`share-whatsapp-${ref.id}`}
                        className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[11px] font-semibold transition-opacity hover:opacity-80"
                        style={{ background: '#25D366', color: '#fff' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          const text = `"${ref.closingLine}"\n\nRead my reflection on ${ref.title} by ${ref.author}\nhttps://thereading-room.com`;
                          window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
                        }}
                      >
                        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                        WhatsApp
                      </button>
                      <button
                        data-testid={`share-linkedin-${ref.id}`}
                        className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[11px] font-semibold transition-opacity hover:opacity-80"
                        style={{ background: '#0A66C2', color: '#fff' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          const text = `"${ref.closingLine}"\n\nMy reflection on ${ref.title} by ${ref.author}\n\nhttps://thereading-room.com`;
                          window.open(`https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent(text)}`, '_blank', 'width=600,height=600');
                        }}
                      >
                        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                        LinkedIn
                      </button>
                    </div>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      </div>

      {/* Reader Stories Section */}
      <section className="px-5 md:px-10 pb-20" style={{ background: 'var(--bg-alt2)' }}>
        <div className="max-w-4xl mx-auto pt-16">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold mb-3" style={{ fontFamily: "'Playfair Display', serif", color: 'var(--text)' }} data-testid="reader-stories-heading">
              From Our Readers
            </h2>
            <p className="text-sm max-w-md mx-auto" style={{ color: 'var(--muted)' }}>
              Thoughts and stories shared by readers of The Reading Room.
            </p>
          </div>

          {loadingStories ? (
            <div className="space-y-4">
              {[1,2,3].map(i => (
                <div key={i} className="animate-pulse rounded-xl p-6" style={{ background: 'var(--bg)' }}>
                  <div className="h-4 rounded w-1/4 mb-3" style={{ background: 'var(--skeleton)' }} />
                  <div className="h-3 rounded w-3/4" style={{ background: 'var(--skeleton)' }} />
                </div>
              ))}
            </div>
          ) : stories.length === 0 ? (
            <div className="text-center py-12 rounded-2xl" style={{ background: 'var(--bg)', border: '1px solid rgba(0,0,0,0.05)' }}>
              <MessageSquare className="w-10 h-10 mx-auto mb-3" style={{ color: 'var(--muted)' }} />
              <p className="text-sm font-medium" style={{ color: 'var(--muted)' }}>No reader stories yet.</p>
              <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>
                Share your thoughts from the <Link to="/" className="underline">homepage</Link>.
              </p>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              {stories.map((s, i) => (
                <div
                  key={s.story_id || i}
                  className="rounded-xl p-5"
                  style={{ background: 'var(--card-bg)', border: '1px solid rgba(0,0,0,0.05)', boxShadow: '0 1px 8px rgba(0,0,0,0.03)' }}
                  data-testid={`reader-story-${i}`}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: 'var(--highlight-muted)' }}>
                      <User className="w-3.5 h-3.5" style={{ color: 'var(--muted)' }} />
                    </div>
                    <span className="text-sm font-semibold" style={{ color: 'var(--text)' }}>{s.name}</span>
                  </div>
                  <p className="text-sm leading-relaxed" style={{ color: 'var(--muted2)' }}>
                    {s.story}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="px-5 md:px-10 py-8 text-center" style={{ background: 'var(--bg)', borderTop: '1px solid rgba(0,0,0,0.06)' }}>
        <Link to="/" className="text-sm font-medium transition-opacity hover:opacity-70" style={{ color: 'var(--muted)' }}>
          Back to The Reading Room
        </Link>
      </footer>
    </div>
  );
}

function ReflectionCover({ cover, title }) {
  const [imgErr, setImgErr] = useState(false);
  return (
    <div className="w-24 sm:w-28 flex-shrink-0">
      {!imgErr ? (
        <img
          src={cover}
          alt={title}
          className="w-full aspect-[2/3] object-cover rounded-xl shadow-md transition-transform duration-500 group-hover:scale-[1.03]"
          onError={() => setImgErr(true)}
        />
      ) : (
        <div className="w-full aspect-[2/3] rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, var(--muted2) 0%, var(--muted) 100%)' }}>
          <BookOpen className="w-8 h-8 text-white/40" />
        </div>
      )}
    </div>
  );
}
